import {useGetAnalysisDetail} from '@domain/rcfa'
import {Close} from '@mui/icons-material'
import {Box, IconButton, LinearProgress, Skeleton, Stack, Typography} from '@mui/material'
import React, {useState} from 'react'

import {useUrlParam} from '../../../hooks/useUrlParam'
import {ImageDetail} from '../../common/Attachment'
import {RcfaCanvasNode} from '../canvas/canvas.types'
import {useNodeDetail} from '../hooks/useNodeDetail'

import {NodeDeleteModal} from './NodeDeleteModal'
import {NodeModalActiveView, NodeDetailType} from './nodeDetail.types'
import {NodeContentSection} from './NodeDetailSections'
import {NodeMenu} from './NodeMenu'

type NodeHeaderProps = {
  canvasNode: RcfaCanvasNode
  DragHandle: JSX.Element
  isLoading: boolean
  data: NodeDetailType | undefined
  canDelete: boolean
  closeModal: () => void
  setActiveView: React.Dispatch<React.SetStateAction<NodeModalActiveView>>
  setRemoveDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const NodeHeader = ({
  canvasNode,
  DragHandle,
  isLoading,
  data,
  canDelete,
  closeModal,
  setActiveView,
  setRemoveDialogOpen
}: NodeHeaderProps) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" paddingRight={2}>
    <Typography
      variant="h2"
      sx={{
        wordWrap: 'break-word',
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start'
      }}
    >
      {DragHandle}
      {canvasNode.title}
    </Typography>
    <Box display="flex" flex="0 0 auto">
      {!isLoading && data && (
        <NodeMenu
          nodeId={canvasNode.id}
          openEditView={() => setActiveView('edit')}
          setRemoveDialogOpened={setRemoveDialogOpen}
          permissions={data.permissions}
          canDelete={canDelete}
          closeDetailModal={closeModal}
        />
      )}
      {isLoading && (
        <Box p="5px">
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
      )}
      <IconButton onClick={() => closeModal()} color="primary">
        <Close />
      </IconButton>
    </Box>
  </Stack>
)

type NodeDetailProps = {
  setActiveView: React.Dispatch<React.SetStateAction<NodeModalActiveView>>
  openTaskDetail: (taskId: string) => void
  canvasNode: RcfaCanvasNode
  DragHandle: JSX.Element
  closeModal: () => void
  setNewNode: React.Dispatch<React.SetStateAction<number | null>>
}

export const NodeDetail = ({
  setActiveView,
  openTaskDetail,
  canvasNode,
  DragHandle,
  closeModal,
  setNewNode
}: NodeDetailProps) => {
  const analysisId = useUrlParam('analysisId')
  const [isCommentActive, setIsCommentActive] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [activeImageId, setActiveImageId] = useState<number | undefined>(undefined)

  const {data: analysisData} = useGetAnalysisDetail(analysisId)
  const {data, isFetching, isPending: isLoading} = useNodeDetail(canvasNode.id.toString())
  const isMainNode = canvasNode.id === 0
  const canDelete = canvasNode?.children ? canvasNode.children.length <= 1 : false
  const showRootNodeDetails = Boolean(
    (analysisData?.stoppage || analysisData?.category || analysisData?.areaTypeCode) && isMainNode
  )

  return (
    <>
      <Stack spacing={2} pb={2}>
        <NodeHeader
          canvasNode={canvasNode}
          DragHandle={DragHandle}
          isLoading={isLoading}
          data={data}
          canDelete={canDelete}
          closeModal={closeModal}
          setActiveView={setActiveView}
          setRemoveDialogOpen={setRemoveDialogOpen}
        />
        {isFetching && !isLoading && <LinearProgress />}
        <NodeContentSection
          canvasNode={canvasNode}
          analysisId={analysisId}
          isLoading={isLoading}
          isMainNode={isMainNode}
          data={data}
          analysisData={analysisData}
          showRootNodeDetails={showRootNodeDetails}
          isCommentActive={isCommentActive}
          setIsCommentActive={setIsCommentActive}
          setActiveView={setActiveView}
          setActiveImageId={setActiveImageId}
          openTaskDetail={openTaskDetail}
        />
      </Stack>
      {data?.permissions?.canDelete && (
        <NodeDeleteModal
          nodeId={data.id}
          analysisId={analysisId}
          title={data.title}
          closeModal={() => setRemoveDialogOpen(false)}
          opened={removeDialogOpen}
          setNewNode={setNewNode}
        />
      )}
      {!!data?.attachments?.length && (
        <ImageDetail
          files={data.attachments}
          activeId={activeImageId}
          setActiveImageId={setActiveImageId}
          nodeId={data.id}
          analysisId={analysisId}
          canDeleteAttachment={data.permissions.canDeleteAttachment}
        />
      )}
    </>
  )
}
